import { useEffect, useState } from 'react'
import { Appearance, type ColorSchemeName } from 'react-native'

export type SystemColorScheme = 'light' | 'dark' | undefined

/** A concrete, resolved color scheme (never undefined). */
export type ColorScheme = 'light' | 'dark'

/**
 * Resolve the active color scheme with this precedence (pure, exported for
 * testing):
 *   1. `controlled` — the `FunkitProvider` `colorScheme` prop. When set, it
 *      wins over everything, so a manual toggle / system change can't move it.
 *   2. `override` — a manual `toggleColorScheme()` choice (uncontrolled only).
 *   3. `system` — the live OS Appearance.
 *   4. `'light'` — fallback when the system scheme is unknown.
 */
export function resolveColorScheme(
  controlled: ColorScheme | undefined,
  override: ColorScheme | undefined,
  system: SystemColorScheme,
): ColorScheme {
  if (controlled) {
    return controlled
  }
  return override ?? system ?? 'light'
}

/**
 * Normalize React Native's `ColorSchemeName` (`'light' | 'dark' | null |
 * undefined`) to `'light' | 'dark' | undefined`. Pure — exported for testing.
 */
export function normalizeColorScheme(
  scheme: ColorSchemeName | null | undefined,
): SystemColorScheme {
  return scheme === 'light' || scheme === 'dark' ? scheme : undefined
}

/**
 * Reads the system color scheme from RN's `Appearance` API and re-renders on
 * change. The RN counterpart to web's `prefers-color-scheme` media query.
 */
export function useColorScheme(): SystemColorScheme {
  const [scheme, setScheme] = useState<SystemColorScheme>(() =>
    normalizeColorScheme(Appearance.getColorScheme()),
  )

  useEffect(() => {
    const subscription = Appearance.addChangeListener(({ colorScheme }) => {
      setScheme(normalizeColorScheme(colorScheme))
    })
    return () => subscription.remove()
  }, [])

  return scheme
}
